import { Plus } from "lucide-react";
import {
  Box,
  Heading,
  Button,
  Spinner,
  Stack,
  Card,
  Field,
  Group,
  Input,
  Checkbox,
  Tag,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Camelize } from "camelize-ts";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../../hooks/useSession";
import { useDebounceState } from "../../../hooks/useDebounceState";
import { useCustomerSearch } from "../../../hooks/useCustomers";
import {
  CustomerGroupBodySchema,
  CustomerGroupSchema,
} from "../../../generated";
import {
  useCustomerGroupCreate,
  useCustomerGroupUpdate,
} from "../../../hooks/useCustomerGroup";

const schema = yup
  .object({
    groupName: yup.string().required(),
    customers: yup.array().of(yup.number().positive().required()).required(),
  })
  .required();

export default function CustomerGroupForm({
  group,
}: {
  group?: Camelize<CustomerGroupSchema>;
}) {
  useSession();
  const navigate = useNavigate();

  const [searchQuery, updateSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounceState(searchQuery);
  const customerSearch = useCustomerSearch(debouncedSearchQuery);
  const customerGroupMutation = useCustomerGroupCreate();
  const customerGroupUpdateMutation = useCustomerGroupUpdate(group?.uuid);

  const {
    register,
    handleSubmit,
    formState: { errors: formStateErrors },
    watch,
    setValue,
    getValues,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    values: {
      groupName: group?.groupName || "",
      customers:
        group?.customers.flatMap((customer) => customer.customerNumber) || [],
    },
  });

  watch("customers");
  watch("groupName");

  useEffect(() => {
    if (customerGroupMutation.isSuccess) {
      navigate("/customers/groups/");
    }
  }, [navigate, customerGroupMutation.isSuccess]);

  const customers = getValues("customers") || [];

  const onSubmit = (data: Camelize<CustomerGroupBodySchema>) => {
    if (group?.uuid) {
      return customerGroupUpdateMutation.mutate(data);
    }
    return customerGroupMutation.mutate(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack>
        <Input {...register("customers")} type="hidden" />
        <Box mt={8}>
          <Field.Root invalid={Boolean(formStateErrors["groupName"])}>
            <Field.Label>Gruppenavn</Field.Label>
            <Input {...register("groupName")} />
          </Field.Root>
        </Box>
        <Stack display="flex" direction="row" gap={8}>
          <Card.Root w={300} minH={300} p={4}>
            <Stack>
              <Heading size="md">Finn kunde</Heading>
              <Field.Root invalid={Boolean(formStateErrors["customers"])}>
                <Group>
                  <Input
                    onChange={(e) => {
                      updateSearchQuery(e.target.value);
                    }}
                    placeholder="Søk"
                  />
                  {customerSearch.isLoading && <Spinner />}
                </Group>
                <Stack gap={4} py={4}>
                  {customerSearch.data?.map((data) => (
                    <Checkbox.Root
                      key={data.customerNumber}
                      bg="bg.muted"
                      p={4}
                      borderRadius="base"
                      checked={customers?.some(
                        (x) => x === data.customerNumber,
                      )}
                      onCheckedChange={(details) => {
                        if (details.checked && data.customerNumber) {
                          setValue("customers", [
                            ...(customers || []),
                            data.customerNumber,
                          ]);
                        } else {
                          setValue("customers", [
                            ...customers.filter(
                              (x) => x !== data.customerNumber,
                            ),
                          ]);
                        }
                      }}
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                      <Checkbox.Label>
                        {data.name}-{data.orgNumber}
                      </Checkbox.Label>
                    </Checkbox.Root>
                  ))}
                  <Button>
                    <Plus />
                  </Button>
                </Stack>
              </Field.Root>
            </Stack>
          </Card.Root>
          <Stack
            display="flex"
            wrap="wrap"
            alignItems="flex-start"
            justifyContent="flex-start"
          >
            {customers.map((user) => (
              <Tag.Root
                key={user}
                fontSize={24}
                height={12}
                p={4}
                borderRadius={8}
              >
                <Tag.Label>Kunde: {user}</Tag.Label>
                <Tag.CloseTrigger
                  onClick={() => {
                    setValue("customers", [
                      ...customers.filter((x) => x !== user),
                    ]);
                  }}
                />
              </Tag.Root>
            ))}
          </Stack>
        </Stack>
        <Button type="submit">
          Lagre kundegruppe {getValues("groupName")}
        </Button>
      </Stack>
    </form>
  );
}
